import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'

import { cn } from '@/lib/utils'
import { telegramConfig } from '@/constants/site'
import { TelegramGlyph } from '@/components/marketplace/telegram-glyph'

/**
 * The closing sell band: one poster-width plate carrying the headline, the
 * primary action and the Telegram community handle, with the red splatter and
 * crown bleeding off the right edge.
 *
 * Everything decorative is aria-hidden, so a screen reader hears exactly two
 * links: "Start selling now" and the community handle.
 */

const COMMUNITY_URL = telegramConfig.communityUrl ?? 'https://t.me/moneyteamalbania'

/** "@handle" read off the community link, so it follows the configured value. */
const communityHandle = (): string => {
    const segment = COMMUNITY_URL.split('?')[0].replace(/\/+$/, '').split('/').pop() ?? ''
    const cleaned = segment.replace(/^@/, '')

    return cleaned ? `@${cleaned}` : '@moneyteamalbania'
}

/** The hand-drawn red arrow that runs off the headline. */
const RedArrow: FC<{ className?: string }> = ({ className }): ReactNode => (
    <svg
        viewBox='0 0 150 40'
        className={className}
        fill='none'
        aria-hidden='true'
        focusable='false'
    >
        <path
            d='M4 21c34-3 76-4 126-3'
            stroke='var(--brand-red)'
            strokeWidth='7'
            strokeLinecap='round'
        />

        <path
            d='M110 5l32 13-32 14'
            stroke='var(--brand-red)'
            strokeWidth='7'
            strokeLinecap='round'
            strokeLinejoin='round'
        />
    </svg>
)

/** The hand-drawn crown, matching the one above the featured grid. */
const Crown: FC<{ className?: string }> = ({ className }): ReactNode => (
    <svg
        viewBox='0 0 64 44'
        className={className}
        fill='none'
        stroke='currentColor'
        strokeWidth='4'
        strokeLinecap='round'
        strokeLinejoin='round'
        aria-hidden='true'
        focusable='false'
    >
        <path d='M5 39C7.2 27.4 6.6 15.8 4 5.5c6.4 6.8 12.4 12 18.4 15.6C25.6 15 28.6 9.4 31.8 3.4c3.6 6.4 6.8 12.2 9.8 17.4C47.4 17.6 53.2 12.6 59.6 6c-2.4 10.6-3 22.2-1 33' />
        <circle cx='60.4' cy='41.2' r='2.2' fill='currentColor' stroke='none' />
    </svg>
)

/** The red paint splatter that fills the right end of the plate. */
const Splatter: FC<{ className?: string }> = ({ className }): ReactNode => (
    <svg
        viewBox='0 0 260 220'
        className={className}
        aria-hidden='true'
        focusable='false'
        preserveAspectRatio='xMidYMid slice'
    >
        <g fill='var(--brand-red)'>
            <path d='M96 12c34-14 78-10 106 12 26 20 34 56 30 90-4 33-22 62-50 78-28 17-66 19-96 5-27-13-45-40-48-71-3-33 12-68 38-89 6-5 13-9 20-13z' opacity='.92' />
            <path d='M52 34c9-6 20 4 15 14-4 9-18 10-22 2-3-6-1-12 7-16z' opacity='.7' />
            <path d='M28 96c8-4 16 5 12 13-4 7-15 7-19 0-3-5-1-10 7-13z' opacity='.55' />
            <path d='M44 168c7-4 15 3 12 11-3 7-13 8-17 2-3-4-2-10 5-13z' opacity='.6' />
            <circle cx='18' cy='140' r='6' opacity='.5' />
            <circle cx='70' cy='196' r='7' opacity='.55' />
            <circle cx='8' cy='62' r='4' opacity='.45' />
            <circle cx='104' cy='208' r='5' opacity='.5' />
        </g>
    </svg>
)

interface SellCtaProps {
    className?: string
}

export const SellCta: FC<SellCtaProps> = ({ className }): ReactNode => {
    return (
        <div
            className={cn(
                'relative overflow-hidden rounded-2xl border-2 border-ink-900 bg-paper shadow-offset',
                className
            )}
        >
            {/* ---- Right-edge artwork ---- */}
            <div className='pointer-events-none absolute inset-y-0 right-0 hidden w-[220px] xl:block' aria-hidden='true'>
                <Splatter className='absolute inset-y-[-10%] right-[-40px] h-[120%] w-[260px]' />
                <Crown className='absolute right-9 top-1/2 h-14 w-auto -translate-y-1/2 text-ink-900' />
            </div>

            <div className='relative grid gap-7 p-6 sm:p-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.75fr)_minmax(0,1fr)] lg:items-center lg:gap-8 lg:p-10 xl:pr-[240px]'>
                {/* ---- Headline ---- */}
                <div className='flex flex-wrap items-center gap-x-5 gap-y-2'>
                    <h2 className='hero-subhead max-w-[10ch] text-ink-900'>
                        Got something to sell?
                    </h2>

                    <RedArrow className='h-8 w-[110px] shrink-0' />
                </div>

                {/* ---- Primary action ---- */}
                <div>
                    <p className='max-w-[26ch] text-sm leading-snug text-text-muted'>
                        List your item on a Bazio marketplace today.
                    </p>

                    <Link
                        href='/sell'
                        className='type-label mta-focus mt-4 inline-flex h-12 items-center justify-center gap-2.5 rounded-md bg-brand-red-ink px-6 text-[13px] text-white transition-colors hover:bg-brand-red-deep'
                    >
                        Start selling now
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </Link>
                </div>

                {/* ---- Community ---- */}
                <a
                    href={COMMUNITY_URL}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='mta-focus group flex items-center gap-4 rounded-xl border-2 border-brand-red bg-paper p-4 transition-colors hover:bg-brand-red/5'
                >
                    <span className='flex size-12 shrink-0 items-center justify-center rounded-full bg-ink-900' aria-hidden='true'>
                        <TelegramGlyph className='size-6 text-paper' />
                    </span>

                    <span className='min-w-0'>
                        <span className='block truncate text-base font-bold text-ink-900'>
                            {communityHandle()}
                        </span>

                        {/*
                            "A community on Bazio", never "the main Telegram
                            community". Bazio hosts many marketplaces and has no
                            single platform-wide group; naming one as THE
                            community presents a tenant as the platform. This is
                            the same wording the header and footer cards use.
                        */}
                        <span className='mt-0.5 block text-sm text-text-muted'>
                            A community on Bazio.
                        </span>
                    </span>
                </a>
            </div>
        </div>
    )
}
